'use client';

import FullWidthSection from '@/components/full-width-section';
import SearchSelect from '@/components/search-select';
import { Input } from '@/components/ui/input';
import { ACADEMY_LIST_LIMIT } from '@/lib/academy-list';
import { resolveFilterRegion } from '@/lib/recommended-store';
import {
  useAcademyList,
  useAcademyRegionList,
} from '@/services/query/academy.query';
import { useRecommendedStore } from '@/services/query/store.query';
import { AcademyRegionEntity } from '@/services/types/academy.type';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import AcademyGrid from './components/academy-grid';
import RecommendedStoreBanner from './components/recommended-store-banner';
import SearchAdBanner from './components/search-ad-banner';

// 전체 선택을 위한 더미 지역 객체
const ALL_REGION: AcademyRegionEntity = {
  value: 'all',
  label: '전체',
  id: 0,
  name: '전체',
  subRegions: [],
};

export default function Client() {
  const searchParams = useSearchParams();

  // URL에서 초기값 가져오기
  const urlPage = searchParams.get('page');

  // SEO 지역 진입값("시도 시군구 읍면동"). 사용자가 필터를 조작하기 전까지
  // 이 값을 백엔드에 넘겨 해당 동 기준 전국 거리순 정렬을 받는다.
  const urlRegion = searchParams.get('region') ?? undefined;

  // keyword 단독(?keyword=) 진입일 때만 검색어로 초기화한다.
  // region이 함께 오면 keyword를 넣지 않고 지역 기준 목록을 그대로 노출한다.
  // (page.tsx의 서버 프리페치 파라미터와 같은 규칙이어야 하이드레이션이 맞물린다)
  const urlKeyword = urlRegion ? '' : (searchParams.get('keyword') ?? '');

  // 드롭다운으로 고른 시/도·시/군/구 id. 아래에서 지역 목록을 받은 뒤 필터로 되살린다.
  const urlSiDo = searchParams.get('siDo');
  const urlSiGunGu = searchParams.get('siGunGu');

  const [page, setPage] = useState(urlPage ? parseInt(urlPage) : 1);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    updateURL({ page: newPage.toString() });
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  // 키워드 검색필터
  const [keywordFilter, setKeywordFilter] = useState<string>(urlKeyword);

  // 키워드 필터 변경 시 URL 업데이트
  useEffect(() => {
    setPage(1);
    updateURL({
      keyword: keywordFilter,
      page: String(page),
    });
  }, [keywordFilter]);

  // URL 업데이트 함수
  const updateURL = (params: Record<string, string | undefined>) => {
    const newSearchParams = new URLSearchParams(searchParams.toString());

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === '') {
        newSearchParams.delete(key);
      } else {
        newSearchParams.set(key, value);
      }
    });

    const newURL = `${window.location.pathname}?${newSearchParams.toString()}`;
    window.history.replaceState({}, '', newURL);
  };

  // 시/도 검색필터
  const [sidoFilter, setSidoFilter] = useState<AcademyRegionEntity>();
  const handleSidoChange = (sido: AcademyRegionEntity) => {
    setSidoFilter(sido);
    setSigunguFilter(undefined);
    setPage(1);
    setKeywordFilter('');
  };

  // 시/도 변경 시 URL 업데이트
  useEffect(() => {
    if (sidoFilter) {
      updateURL({
        siDo: sidoFilter.value === 'all' ? undefined : sidoFilter.value,
        siGunGu: undefined,
        keyword: undefined,
        page: '1',
      });
    }
  }, [sidoFilter]);

  //  시/군/구 검색필터
  const [sigunguFilter, setSigunguFilter] = useState<AcademyRegionEntity>();
  const handleSigunguChange = (sigungu: AcademyRegionEntity) => {
    setSigunguFilter(sigungu);
    setPage(1);
    setKeywordFilter('');
  };

  // 시/군/구 변경 시 URL 업데이트
  useEffect(() => {
    if (sigunguFilter) {
      updateURL({
        siGunGu: sigunguFilter.value,
        keyword: undefined,
        page: '1',
      });
    }
  }, [sigunguFilter]);

  const { data: academyList } = useAcademyList({
    page,
    limit: ACADEMY_LIST_LIMIT,
    siDo: sidoFilter?.value === 'all' ? undefined : sidoFilter?.id,
    siGunGu: sigunguFilter?.id,
    keyword: keywordFilter,
    // 사용자가 시/도·시/군/구·키워드 필터를 조작하면 지역 거리순 대신 기존 필터 동작으로 전환
    region:
      sidoFilter || sigunguFilter || keywordFilter ? undefined : urlRegion,
  });
  const academies = academyList?.data?.academies || [];
  const totalCount = academyList?.data?.totalCount || 0;

  const { data: regionList } = useAcademyRegionList();
  const siDoRegions = regionList?.data || [];

  // 추천 실내운전연습장 기준 지역: 시/군/구 > 시/도 > URL 지역 순.
  // '전체' 선택 시 기준이 없고, 키워드 검색은 지역을 바꾸지 않으므로 기존 기준을 유지한다.
  // 필터를 되살리기 전 첫 렌더에도 URL siDo·siGunGu 기준 지역을 써서, 서버(page.tsx)가
  // 미리 받은 지점을 그대로 그린다(지점이 있는데 광고가 먼저 보이지 않게).
  const recommendRegion =
    sidoFilter?.value === 'all'
      ? undefined
      : sidoFilter
        ? [sidoFilter.name, sigunguFilter?.name].filter(Boolean).join(' ')
        : (urlRegion ??
          resolveFilterRegion(regionList?.data, urlSiDo, urlSiGunGu)?.name);
  const recommendedStoreQuery = useRecommendedStore(recommendRegion);
  const recommendedStore = recommendedStoreQuery.data;
  // 지역이 바뀌어 지점을 다시 찾는 동안에는 이전 결과(placeholderData)로 광고나 이전 지역 지점을
  // 그리지 않는다(지점이 있는 지역인데 광고가 먼저 보였다).
  const isStoreSettled =
    recommendedStoreQuery.isSuccess && !recommendedStoreQuery.isPlaceholderData;
  // 기준 지역이 없거나, 반경 내 매장이 없거나, 조회에 실패하면 광고 배너를 노출한다.
  const showAdBanner =
    !recommendRegion ||
    recommendedStoreQuery.isError ||
    (isStoreSettled && recommendedStore === null);

  // 시/도·시/군/구 선택은 URL(siDo·siGunGu)에만 남아 새로고침·공유 링크로 들어오면
  // 필터가 비어 전국 목록이 떴다. 지역 목록을 받으면 한 번만 URL 값으로 필터를 되살린다.
  const regionRestoredRef = useRef(false);
  useEffect(() => {
    if (regionRestoredRef.current || !regionList?.data) return;
    regionRestoredRef.current = true;
    const sido = regionList.data.find((region) => region.value === urlSiDo);
    if (!sido) return;
    setSidoFilter(sido);
    setSigunguFilter(
      sido.subRegions?.find((region) => region.value === urlSiGunGu),
    );
  }, [regionList]);

  // 전체 옵션을 포함한 시/도 목록
  const totalSiDoRegions = [ALL_REGION, ...siDoRegions];

  // 전체 선택 여부 확인
  const isAllSelected = sidoFilter?.value === 'all';
  return (
    <>
      {/* 검색 컴포넌트 - 웹 */}
      <FullWidthSection className="desktop:block bg-bg-grayblue relative mx-auto hidden h-[120px]">
        <div className="web-w relative mx-auto">
          <div className="absolute top-0 left-0 flex w-full gap-3">
            <div className="flex items-center justify-center gap-3 pr-0 pb-0">
              <SearchSelect
                triggerPlaceholder={'시/도 선택'}
                selected={sidoFilter ? [sidoFilter] : []}
                onFilterChange={(filter) =>
                  handleSidoChange(filter as AcademyRegionEntity)
                }
                list={totalSiDoRegions}
                triggerAddClass="!h-[66px] !w-[191px]"
              />
              <SearchSelect
                triggerPlaceholder={'시/군/구 선택'}
                selected={sigunguFilter ? [sigunguFilter] : []}
                onFilterChange={(filter) =>
                  handleSigunguChange(filter as AcademyRegionEntity)
                }
                list={siDoRegions
                  .filter((region) => region.value === sidoFilter?.value)
                  .flatMap((region) => region.subRegions ?? [])}
                triggerAddClass="!h-[66px] !w-[191px]"
                disabled={isAllSelected}
              />
            </div>
            <Input
              value={keywordFilter}
              onChange={(e) => setKeywordFilter(e.target.value)}
              placeholder={'지역 및 학원명을 입력해주세요.'}
              className="border-stroke-white placeholder-level-6 text-level-3 !typo-18 h-[66px] w-full !rounded-md bg-white px-6 py-5 !font-semibold"
            />
          </div>
        </div>
      </FullWidthSection>

      {/* 검색 컴포넌트 - 모바일 */}
      <FullWidthSection className="bg-bg-graylight desktop:hidden">
        <div className="desktop:flex-wrap desktop:gap-0 flex flex-col gap-2 px-7 py-6">
          <div className="flex items-center justify-center gap-3">
            <SearchSelect
              triggerPlaceholder={'시/도 선택'}
              selected={sidoFilter ? [sidoFilter] : []}
              onFilterChange={(filter) =>
                handleSidoChange(filter as AcademyRegionEntity)
              }
              list={totalSiDoRegions}
              triggerAddClass="!h-[54px]"
            />
            <SearchSelect
              triggerPlaceholder={'시/군/구 선택'}
              selected={sigunguFilter ? [sigunguFilter] : []}
              onFilterChange={(filter) =>
                handleSigunguChange(filter as AcademyRegionEntity)
              }
              list={siDoRegions
                .filter((region) => region.value === sidoFilter?.value)
                .flatMap((region) => region.subRegions ?? [])}
              triggerAddClass="!h-[54px]"
              disabled={isAllSelected}
            />
          </div>
          <Input
            value={keywordFilter}
            onChange={(e) => setKeywordFilter(e.target.value)}
            placeholder={'지역 및 학원명을 입력해주세요.'}
            className="border-stroke-white placeholder-level-6 text-level-3 !typo-18 h-[54px] w-full !rounded-md bg-white px-6 py-5 !font-semibold"
          />
        </div>
      </FullWidthSection>

      {/* 검색창 아래: 근처 추천 실내운전연습장, 없으면 광고 배너 */}
      {showAdBanner ? (
        <SearchAdBanner />
      ) : isStoreSettled && recommendedStore && recommendRegion ? (
        <RecommendedStoreBanner
          store={recommendedStore}
          region={recommendRegion}
        />
      ) : (
        // 지점 조회 중: 배너 높이만큼 자리를 비워 두어 결과가 오면 아래 목록이 밀리지 않게 한다.
        <div aria-hidden className="desktop:flex desktop:justify-center">
          <div className="desktop:web-w desktop:mt-8 desktop:h-[212px] desktop:w-full mt-4 h-[112px]" />
        </div>
      )}

      <div className="desktop:flex desktop:justify-center">
        <div className="desktop:web-w desktop:w-full">
          {/* 검색 결과 카운트 */}
          <div className="text-level-2 typo-20 desktop:typo-28 desktop:pb-7 desktop:pt-14.5 desktop:font-semibold flex items-center pt-10.5 pb-4 font-extrabold">
            <div className="relative size-6">
              <Image
                alt="map-pin"
                src={'/images/icons/icon_map_pin.svg'}
                fill
                className="mr-1"
              />
            </div>
            <span className="text-main-blue font-extrabold">
              {totalCount}개의 학원
            </span>
            이 검색되었어요.
          </div>
          <AcademyGrid
            academies={academies}
            pageSize={ACADEMY_LIST_LIMIT}
            totalCount={totalCount}
            page={page}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </>
  );
}
